import ServiceNavbar from '../shared/ServiceNavbar';
import ServiceHero from '../shared/ServiceHero';
import ServiceProcess from '../shared/ServiceProcess';
import ServiceFAQ from '../shared/ServiceFAQ';
import ServiceCTABlock from '../shared/ServiceCTABlock';
import Footer from '../../home/components/Footer';

const processSteps = [
  { icon: 'ri-search-eye-fill', step: 'Step 01', title: 'System Assessment', description: 'We evaluate your current panel capacity, wiring condition, and any solar compatibility requirements.' },
  { icon: 'ri-draft-fill', step: 'Step 02', title: 'Engineering Design', description: 'We design the right panel solution — load calculations, breaker configuration, and upgrade path.' },
  { icon: 'ri-file-shield-2-fill', step: 'Step 03', title: 'Permit & Utility Notice', description: 'Permits submitted, utility company notified, and all documentation prepared before work begins.' },
  { icon: 'ri-tools-fill', step: 'Step 04', title: 'Installation', description: 'New panel installed, circuits transferred safely, solar interconnect or subpanel added as needed.' },
  { icon: 'ri-checkbox-circle-fill', step: 'Step 05', title: 'Final Inspection', description: 'Full inspection passed, labeling completed, homeowner walkthrough, and as-built documentation provided.' },
];

const faqs = [
  { question: 'How do I know if my panel needs an upgrade?', answer: 'Signs include frequently tripping breakers, a 100A or smaller service entrance, Federal Pacific or Zinsco panels (which have known safety issues), no room for new circuits, or plans to add EV charging, solar, or heavy appliances. We can assess your current panel and give you an honest recommendation.' },
  { question: 'What panel size do most homes need?', answer: 'Most modern homes need a 200A service. Homes with EV chargers, solar, electric vehicle fleets, or whole-home electrification may benefit from 320A or 400A service. We calculate your actual load requirements to give you the right recommendation — not just the biggest sale.' },
  { question: 'Can my panel support solar panel installation?', answer: 'It depends on your current panel\'s size, age, and remaining capacity. A panel upgrade may be required before solar can be safely connected. We assess solar compatibility and install the solar-ready infrastructure the system will need.' },
  { question: 'Is a panel upgrade disruptive to my home?', answer: 'The process does require a temporary power shutoff — typically for 4–8 hours. We schedule work to minimize inconvenience and communicate the timeline clearly in advance. Most upgrades are completed in a single day.' },
  { question: 'Do you work with solar installers?', answer: 'Yes. We regularly coordinate with solar installation companies to provide the electrical infrastructure, interconnect, and metering configurations they need for a complete system. If you\'re planning solar, we can work directly with your solar provider.' },
];

const SolarPanelPage = () => (
  <div className="min-h-screen bg-[#060E1C]">
    <ServiceNavbar />

    <ServiceHero
      bgImage="https://public.readdy.ai/ai/img_res/741e385ecbd5066cbeec5ccd2a1ac199.jpg"
      label="Solar Ready & Panel Upgrades"
      headline="Upgrade Your"
      headlineAccent="Power Infrastructure"
      subtext="Expert electrical panel upgrades and solar-ready infrastructure for Kalamazoo homes. Increase capacity, improve safety, and future-proof your electrical system for the energy demands of today and tomorrow."
      trustBadges={[
        { icon: 'ri-sun-fill', label: 'Solar-Ready' },
        { icon: 'ri-shield-star-fill', label: 'Licensed & Insured' },
        { icon: 'ri-code-s-slash-fill', label: 'Code-Compliant' },
        { icon: 'ri-award-fill', label: '20+ Years Experience' },
      ]}
    />

    {/* Technical Overview */}
    <section className="relative bg-[#070E1A] py-24 overflow-hidden">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-[0.035] pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="tech-grid" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
              <path d="M 50 0 L 0 0 0 50" fill="none" stroke="#D97706" strokeWidth="0.6"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#tech-grid)"/>
        </svg>
      </div>
      <div className="absolute top-0 left-1/3 w-[400px] h-[400px] rounded-full bg-[#D97706]/5 blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Main text — 2 cols */}
          <div className="lg:col-span-2">
            <span className="inline-block text-[#D97706] text-[11px] font-semibold uppercase tracking-[3px] mb-5">The Service</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-6 leading-tight">
              Your Home&apos;s Electrical System<br />Is the Foundation of Everything
            </h2>
            <p className="text-[#5E7A96] text-[15px] leading-relaxed mb-5">
              Modern electrical demand has never been higher — EV chargers, heat pumps, solar inverters, smart home systems, and growing appliance loads are pushing older panels well beyond their original design. A proper panel upgrade is the most impactful electrical investment you can make.
            </p>
            <p className="text-[#5E7A96] text-[15px] leading-relaxed mb-8">
              DOC Electric performs panel upgrades with the level of precision and documentation that inspectors and utility companies expect. We upgrade service entrances, replace aged equipment, configure solar-ready infrastructure, and leave your system organized, labeled, and inspection-ready.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                '100A to 200A service upgrades',
                '200A to 400A service upgrades',
                'Federal Pacific / Zinsco panel replacements',
                'Solar-ready panel and inverter prep',
                'Subpanel installation for garages and additions',
                'Whole-home rewiring for aged homes',
                'Tandem breaker and load center optimization',
                'Utility coordination and meter socket upgrade',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-5 h-5 flex items-center justify-center rounded flex-shrink-0 bg-[#D97706]/15">
                    <i className="ri-check-line text-[#D97706] text-xs" />
                  </span>
                  <span className="text-[#8BA8C8] text-[13px]">{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Capacity chart sidebar */}
          <div className="flex flex-col gap-4">
            <div className="bg-[#0A1628] rounded-xl border border-[#1E3048] p-6">
              <h4 className="text-white text-[13px] font-bold uppercase tracking-wider mb-5">Panel Size Guide</h4>
              {[
                { size: '100A', label: 'Older Homes', suitable: false, desc: 'Often insufficient for modern loads' },
                { size: '200A', label: 'Most Homes', suitable: true, desc: 'Handles most residential needs' },
                { size: '320A', label: 'EV + Solar', suitable: true, desc: 'Ideal for heavy loads & solar' },
                { size: '400A', label: 'Full Electrification', suitable: true, desc: 'Maximum future-proofing' },
              ].map((row) => (
                <div key={row.size} className="flex items-start gap-3 py-3 border-b border-[#1E3048] last:border-0">
                  <span className={`text-[15px] font-black w-12 flex-shrink-0 ${row.suitable ? 'text-[#D97706]' : 'text-[#3A566E]'}`}>{row.size}</span>
                  <div>
                    <div className="text-white text-[12px] font-semibold">{row.label}</div>
                    <div className="text-[#4A6A86] text-[11px]">{row.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <img
              src="https://readdy.ai/api/search-image?query=solar%20panel%20array%20on%20residential%20roof%20with%20disconnect%20switch%20electrical%20infrastructure%20modern%20home%20clean%20sunlight%20bright%20clean%20installation%20professional%20residential%20solar%20panels%20neat%20rows%20sunlight&width=600&height=350&seq=sp-img1&orientation=landscape"
              alt="Solar ready home"
              className="w-full h-44 object-cover object-top rounded-xl border border-[#1E3048]"
            />
          </div>
        </div>
      </div>
    </section>

    <ServiceProcess
      title="Panel Upgrade & Solar Prep Process"
      subtitle="Structured, documented, and inspection-ready at every step."
      steps={processSteps}
      theme="dark"
      accentColor="#D97706"
    />

    {/* Benefits — data-focused */}
    <section className="bg-[#111C2D] py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-[#D97706] text-[11px] font-semibold uppercase tracking-[3px] mb-5">Long-Term Value</span>
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-6 leading-tight">
              An Investment That Pays<br />in Safety and Value
            </h2>
            <p className="text-[#5E7A96] text-[15px] leading-relaxed mb-8">
              A modern panel upgrade improves electrical safety, enables high-demand modern equipment, and can meaningfully increase your home&apos;s resale value. It&apos;s one of the most practical infrastructure investments a homeowner can make.
            </p>
            <div className="flex flex-col gap-5">
              {[
                { icon: 'ri-home-fill', stat: '+$10,000+', label: 'Average home value increase', desc: 'Modernized electrical systems are a top buyer preference in real estate.' },
                { icon: 'ri-fire-fill', stat: '↓ 67%', label: 'Reduced electrical fire risk', desc: 'Panel upgrades eliminate the most common causes of residential electrical fires.' },
                { icon: 'ri-sun-fill', stat: '100%', label: 'Solar-ready after upgrade', desc: 'A modern panel is the prerequisite for any solar, EV, or heat pump installation.' },
              ].map((item) => (
                <div key={item.label} className="flex gap-5 p-5 bg-[#0A1628] rounded-xl border border-[#1E3048]">
                  <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#D97706]/12 flex-shrink-0">
                    <i className={`${item.icon} text-[#D97706] text-lg`} />
                  </span>
                  <div>
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-[#D97706] font-black text-xl">{item.stat}</span>
                      <span className="text-white text-[13px] font-semibold">{item.label}</span>
                    </div>
                    <p className="text-[#4A6A86] text-[13px]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <img
              src="https://readdy.ai/api/search-image?query=new%20modern%20residential%20electrical%20panel%20200%20amp%20service%20main%20breaker%20box%20organized%20circuit%20breakers%20labeled%20clean%20installation%20professional%20dark%20navy%20dramatic%20lighting%20technical%20precision%20craftsmanship&width=700&height=700&seq=sp-panel1&orientation=squarish"
              alt="Modern electrical panel upgrade"
              className="w-full h-[500px] object-cover object-top rounded-2xl border border-[#1E3048]"
            />
          </div>
        </div>
      </div>
    </section>

    <ServiceFAQ faqs={faqs} theme="dark" />
    <ServiceCTABlock
      servicePreset="Solar & Panel Upgrade"
      headline="Ready to Upgrade?"
      subtext="Talk to our panel upgrade specialists. We'll assess your current system and give you an honest, code-compliant plan to get your electrical infrastructure where it needs to be."
    />
    <Footer />
  </div>
);

export default SolarPanelPage;
